import type { TFunction } from "i18next";
import { utils, write } from "xlsx";
import {
  ProjectOverviewScope,
  ProjectStatus,
  type ProjectOverviewDto,
  type ProjectOverviewRowDto,
} from "../../autogen/bff-api";
import { saveBlob } from "../../common/download/downloadBffFile";
import { displayPersonName } from "../../common/persons/displayPersonName";
import { projectStatusLabel } from "../../common/projects/useProjectStatuses";
import { sanitizeFilename } from "../../common/utils/sanitizeUtils";
import { urls } from "../../urls";
import { contractValueFigure } from "../projects/view-single/ProcurementSummary";

export const STATUS_PARAM = "status";
export const TYPE_PARAM = "type";
export const SHOW_PARAM = "show";

const SCOPE_IN_ADDRESS: Record<ProjectOverviewScope, string | null> = {
  [ProjectOverviewScope.Ongoing]: "ongoing",
  [ProjectOverviewScope.NotStarted]: "not-started",
  [ProjectOverviewScope.All]: null,
};

export const SCOPES: ProjectOverviewScope[] = [
  ProjectOverviewScope.Ongoing,
  ProjectOverviewScope.NotStarted,
  ProjectOverviewScope.All,
];

export const scopeFromAddress = (value: string | null): ProjectOverviewScope =>
  SCOPES.find((scope) => SCOPE_IN_ADDRESS[scope] === value) ?? ProjectOverviewScope.All;

export const scopeInAddress = (scope: ProjectOverviewScope): string | null => SCOPE_IN_ADDRESS[scope];

export const scopeLabel = (t: TFunction, scope: ProjectOverviewScope): string =>
  ({
    [ProjectOverviewScope.Ongoing]: t("Ongoing"),
    [ProjectOverviewScope.NotStarted]: t("Not started"),
    [ProjectOverviewScope.All]: t("All projects"),
  })[scope];

export type ProjectFigure =
  | "ongoing"
  | "startingSoon"
  | "withoutSourcingResponsible"
  | "contractValue"
  | "endingBeforeProjectEnd"
  | "productsWithoutProcurement";

export const PROJECT_FIGURES: ProjectFigure[] = [
  "ongoing",
  "startingSoon",
  "withoutSourcingResponsible",
  "contractValue",
  "endingBeforeProjectEnd",
  "productsWithoutProcurement",
];

export const figureFromAddress = (value: string | null): ProjectFigure | null =>
  PROJECT_FIGURES.find((figure) => figure === value) ?? null;

export const isBehindFigure = (figure: ProjectFigure, row: ProjectOverviewRowDto): boolean => {
  switch (figure) {
    case "ongoing":
      return row.status === ProjectStatus.Ongoing;
    case "startingSoon":
      return row.startsSoon;
    case "withoutSourcingResponsible":
      return row.withoutSourcingResponsible;
    case "contractValue":
      return (row.contracts?.contracts ?? 0) > 0;
    case "endingBeforeProjectEnd":
      return (row.contractsEndingBeforeProjectEnd ?? 0) > 0;
    case "productsWithoutProcurement":
      return row.products.total > row.products.withProcurement;
  }
};

export const projectFigureLabel = (t: TFunction, figure: ProjectFigure): string =>
  ({
    ongoing: t("Ongoing projects"),
    startingSoon: t("Starting within 90 days"),
    withoutSourcingResponsible: t("Without a sourcing responsible"),
    contractValue: t("Total contract value"),
    endingBeforeProjectEnd: t("Expires before the project ends"),
    productsWithoutProcurement: t("Products without a procurement"),
  })[figure];

export const projectFigureDefinition = (t: TFunction, figure: ProjectFigure, startsSoonUntil: string): string =>
  ({
    ongoing: t(
      "Projects whose start date has passed, or that have no start date, and whose end date has not passed, or that have no end date."
    ),
    startingSoon: t("Projects that have not started and start within the next 90 days, on or before {{date}}.", {
      date: startsSoonUntil,
    }),
    withoutSourcingResponsible: t("Projects with no sourcing responsible, or whose sourcing responsible is deactivated."),
    contractValue: t(
      "The sum of the total price of the contracts linked to the projects. A contract linked to several projects counts once."
    ),
    endingBeforeProjectEnd: t(
      "Active contracts in the projects whose end date is before the project's end date. A contract in several projects counts once."
    ),
    productsWithoutProcurement: t("Products in the projects' product lists that are not in a procurement yet."),
  })[figure];

export const projectFigureValue = (figure: ProjectFigure, overview: ProjectOverviewDto): string => {
  const { figures } = overview;
  switch (figure) {
    case "ongoing":
      return String(figures.ongoing);
    case "startingSoon":
      return String(figures.startingSoon);
    case "withoutSourcingResponsible":
      return String(figures.withoutSourcingResponsible);
    case "contractValue":
      return figures.contractValue ? contractValueFigure(figures.contractValue) : NOT_SHOWN;
    case "endingBeforeProjectEnd":
      return figures.contractsEndingBeforeProjectEnd == null ? NOT_SHOWN : String(figures.contractsEndingBeforeProjectEnd);
    case "productsWithoutProcurement":
      return String(figures.productsWithoutProcurement);
  }
};

export const NOT_SHOWN = "–";

export const projectFigureLink = (current: URLSearchParams, figure: ProjectFigure): string => {
  const params = new URLSearchParams(current);
  params.set(SHOW_PARAM, figure);
  return `${urls.app.overview}?${params}`;
};

export const projectName = (t: TFunction, row: Pick<ProjectOverviewRowDto, "visible" | "name">): string =>
  row.visible && row.name != null ? row.name : t("A project you cannot access");

export const periodText = (
  row: Pick<ProjectOverviewRowDto, "startDate" | "endDate">,
  displayDate: (date: string) => string,
): string =>
  `${row.startDate ? displayDate(row.startDate) : NOT_SHOWN} – ${row.endDate ? displayDate(row.endDate) : NOT_SHOWN}`;

export const projectOverviewSheetRows = (
  t: TFunction,
  rows: ProjectOverviewRowDto[],
  heading: { retrieved: string },
  displayDate: (date: string) => string,
): (string | number)[][] => [
  [String(t("Retrieved {{date}}", { date: heading.retrieved }))],
  [String(t("Based on what you have access to"))],
  [],
  [
    t("Project number"),
    t("Project"),
    t("Type"),
    t("Status"),
    t("Start date"),
    t("End date"),
    t("Sourcing responsible"),
    t("Contracts"),
    t("Total price"),
    t("Contracts without a total price"),
    t("Ongoing procurements"),
    t("Products with a procurement"),
    t("Products"),
    t("Starts soon without a sourcing responsible"),
  ].map(String),
  ...rows.map((row) => [
    row.externalId ?? "",
    projectName(t, row),
    row.projectType ? String(t(row.projectType)) : "",
    projectStatusLabel(row.status, t),
    row.startDate ? displayDate(row.startDate) : "",
    row.endDate ? displayDate(row.endDate) : "",
    row.sourcingResponsible ? displayPersonName(row.sourcingResponsible).trim() : "",
    row.contracts?.contracts ?? "",
    row.contracts ? contractValueFigure(row.contracts) : "",
    row.contracts?.withoutTotalPrice ?? "",
    row.ongoingProcurements ?? "",
    row.products.withProcurement,
    row.products.total,
    String(row.startsSoonWithoutSourcingResponsible ? t("Yes") : t("No")),
  ]),
];

export const downloadProjectOverview = (
  t: TFunction,
  rows: ProjectOverviewRowDto[],
  heading: { retrieved: string },
  displayDate: (date: string) => string,
) => {
  const workbook = utils.book_new();
  utils.book_append_sheet(
    workbook,
    utils.aoa_to_sheet(projectOverviewSheetRows(t, rows, heading, displayDate)),
    String(t("Projects")),
  );
  saveBlob(
    new Blob([write(workbook, { bookType: "xlsx", type: "buffer" })], { type: "application/octet-stream" }),
    `${sanitizeFilename(String(t("Projects")))}.xlsx`,
  );
};
